"use client"
/* eslint-disable react-hooks/preserve-manual-memoization -- compiler cannot preserve existing useCallback patterns; code works, just not compiler-optimized */

import { useRef, useState, useCallback, useEffect } from "react"
import { usePathname } from "next/navigation"
import {
  MessageSquare,
  X,
  Minus,
  Maximize2,
  Minimize2,
  GripHorizontal,
  Plus,
  Compass,
  PanelLeftOpen,
  PanelLeftClose,
  ChevronDown,
  Search,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { useChatContext } from "@/lib/ai-chat/chat-context"
import { useAiCredits } from "@/hooks/ai/use-ai-credits"
import type { TextDirection } from "@/lib/i18n/direction"
import { ChatMessageList } from "./chat-message-list"
import { ChatInput } from "./chat-input"
import { ChatWelcome } from "./chat-welcome"
import { ChatSidebar } from "./chat-sidebar"
import { ChatAiNotices } from "./chat-ai-notices"
import { VerificationDialog } from "./verification-dialog"
import { CompactWelcome } from "./compact-welcome"
import { FULL_PAGE_CHAT_PATH } from "@/lib/ai-chat/full-page-chat"

/**
 * Arrow toward the top inline-end corner — indicates "dock to side panel". The
 * drawer docks at the inline end, so the arrow mirrors in right-to-left.
 */
function DockRightIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={cn("rtl-flip", className)}>
      <polyline points="7 17 17 7" />
      <polyline points="7 7 17 7 17 17" />
    </svg>
  )
}

// ─── Snap corners ────────────────────────────────────────────────────────────
type Corner = "bottom-right" | "bottom-left" | "top-right" | "top-left"

// A 380x500 PiP left ~298px of readable transcript once the header, the input
// shell and the bubble padding were subtracted — the reported "output window
// very small". The default is now sized for reading and only shrinks when the
// viewport genuinely cannot hold it.
export const DEFAULT_W = 560
export const DEFAULT_H = 640
export const MIN_W = 340
export const MIN_H = 400
const MAX_W = 1000
const MAX_H = 1000
const MARGIN = 16

export type PipResizeEdge = "t" | "b" | "l" | "r" | "tl" | "tr" | "bl" | "br"
export interface PipGeometry { x: number; y: number; w: number; h: number }

/**
 * Derive the complete next PiP rectangle in one calculation. The readable
 * minimum applies only when the viewport can contain it; containment wins on
 * phone-sized or shrunken windows.
 */
export function calculatePipGeometry(
  start: PipGeometry,
  viewport: { w: number; h: number },
  resize?: { edge: PipResizeEdge; dx: number; dy: number },
): PipGeometry {
  const marginX = viewport.w >= MARGIN * 2 ? MARGIN : 0
  const marginY = viewport.h >= MARGIN * 2 ? MARGIN : 0
  const availableW = Math.max(0, viewport.w - marginX * 2)
  const availableH = Math.max(0, viewport.h - marginY * 2)

  let desiredW = start.w
  let desiredH = start.h
  if (resize?.edge.includes("r")) desiredW = start.w + resize.dx
  if (resize?.edge.includes("l")) desiredW = start.w - resize.dx
  if (resize?.edge.includes("b")) desiredH = start.h + resize.dy
  if (resize?.edge.includes("t")) desiredH = start.h - resize.dy

  const maxW = Math.min(MAX_W, availableW)
  const maxH = Math.min(MAX_H, availableH)
  const minW = Math.min(MIN_W, maxW)
  const minH = Math.min(MIN_H, maxH)
  const w = Math.min(maxW, Math.max(minW, desiredW))
  const h = Math.min(maxH, Math.max(minH, desiredH))

  const desiredX = resize?.edge.includes("l") ? start.x + start.w - w : start.x
  const desiredY = resize?.edge.includes("t") ? start.y + start.h - h : start.y
  const maxX = Math.max(marginX, viewport.w - marginX - w)
  const maxY = Math.max(marginY, viewport.h - marginY - h)

  return {
    x: Math.min(maxX, Math.max(marginX, desiredX)),
    y: Math.min(maxY, Math.max(marginY, desiredY)),
    w,
    h,
  }
}

/**
 * Clamp a PiP size to both the resize limits AND the viewport, so a default
 * that is comfortable on a laptop can never open off-screen on a small window.
 */
export function clampPipSize(
  w: number,
  h: number,
  vw: number,
  vh: number,
): { w: number; h: number } {
  const next = calculatePipGeometry(
    { x: MARGIN, y: MARGIN, w, h },
    { w: vw, h: vh },
  )
  return { w: next.w, h: next.h }
}

/**
 * Opening width for the docked drawer when the user has never resized it.
 * The context default (420) is barely wider than a phone; a fraction of the
 * viewport keeps the transcript readable on any monitor. The context clamps
 * the result to its own DRAWER_MIN/DRAWER_MAX.
 */
export function getInitialDrawerWidth(vw: number): number {
  return Math.max(480, Math.round(vw * 0.42))
}

/**
 * How much wider (px; negative = narrower) the docked drawer gets while its
 * resize handle is dragged from `startX` to `currentX`. The drawer docks at the
 * inline end and the handle sits on the edge that faces the page, so moving
 * the pointer toward the page widens it: leftwards in left-to-right,
 * rightwards in right-to-left.
 */
export function drawerWidthDelta(startX: number, currentX: number, direction: TextDirection): number {
  return direction === "rtl" ? currentX - startX : startX - currentX
}

/**
 * The direction the stylesheet lays `element` out in: its computed
 * `direction`, or the nearest `dir` attribute where styles are not computed.
 * The drawer's `end-0` follows <html dir> from the first paint, while the
 * React locale reads English until a saved Arabic catalogue has loaded, so
 * the drag reads the side from the page rather than from the locale.
 */
function layoutDirectionOf(element: Element): TextDirection {
  const computed = element.ownerDocument.defaultView?.getComputedStyle(element).direction
  if (computed === "rtl" || computed === "ltr") return computed
  return element.closest("[dir]")?.getAttribute("dir") === "rtl" ? "rtl" : "ltr"
}

function getSavedSize(): { w: number; h: number } {
  if (typeof window === "undefined") return { w: DEFAULT_W, h: DEFAULT_H }
  try {
    const saved = localStorage.getItem("ai-chat-pip-size")
    if (saved) {
      const parsed = JSON.parse(saved)
      return clampPipSize(
        parsed.w || DEFAULT_W,
        parsed.h || DEFAULT_H,
        window.innerWidth,
        window.innerHeight,
      )
    }
  } catch { /* ignore */ }
  return clampPipSize(DEFAULT_W, DEFAULT_H, window.innerWidth, window.innerHeight)
}

function getCornerPosition(corner: Corner, w: number, h: number): { x: number; y: number } {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200
  const vh = typeof window !== "undefined" ? window.innerHeight : 800

  switch (corner) {
    case "bottom-right":
      return { x: vw - w - MARGIN, y: vh - h - MARGIN }
    case "bottom-left":
      return { x: MARGIN, y: vh - h - MARGIN }
    case "top-right":
      return { x: vw - w - MARGIN, y: MARGIN }
    case "top-left":
      return { x: MARGIN, y: MARGIN }
  }
}

function nearestCorner(x: number, y: number, w: number, h: number): Corner {
  const vw = window.innerWidth
  const vh = window.innerHeight
  const cx = x + w / 2
  const cy = y + h / 2
  const isRight = cx > vw / 2
  const isBottom = cy > vh / 2
  if (isBottom && isRight) return "bottom-right"
  if (isBottom && !isRight) return "bottom-left"
  if (!isBottom && isRight) return "top-right"
  return "top-left"
}

// Pre-auth + non-tenant surfaces where ChatCompanion must NOT render. The
// panel's data-path requires a workspace-scoped session, and surfacing it
// on the unauthenticated login screens is both wrong UX and broken
// functionality (every prompt would 401).
//
// FIX (portal-manager-fix-wave-1, 2026-05-12): replaced the private prefix
// list with the shared lib/routing/hidden-route-prefixes module so the three
// chrome consumers (HeaderControls, FeedbackWidget, ChatCompanion) can never
// drift again. See that file for the canonical list.
import { isHiddenChromeRoute } from "@/lib/routing/hidden-route-prefixes"

// ─── Component ───────────────────────────────────────────────────────────────
export function ChatCompanion() {
  const pathname = usePathname()
  const ctx = useChatContext()

  // ── AI standing, fetched ONLY while a panel is actually open ─────────────
  // This component is mounted on every authenticated page and returns null when
  // `companionMode === "closed"` — but the early return happens AFTER the hooks
  // run, so an unconditional `useAiCredits()` here would put a 60-second poll of
  // /me/ai-allowance behind the whole product for a panel nobody opened.
  const chatOpen = ctx.companionMode !== "closed"
  const { allowance, isBlocked } = useAiCredits({ enabled: chatOpen })

  // True ONLY when the server says the ceiling is reached AND says it is
  // enforcing it. Observe mode measures without blocking; `unknown` never
  // blocks. Shared by all three variants below so they cannot drift again.
  const composerDisabled = ctx.apiKeyMissing || isBlocked
  const aiNotices = (
    <ChatAiNotices
      allowance={allowance}
      error={ctx.chatError}
      apiKeyMissing={ctx.apiKeyMissing}
      className="px-3 pt-3"
    />
  )

  // Hide completely on the dedicated AI chat page
  const isOnChatPage = pathname === FULL_PAGE_CHAT_PATH

  // Hide on pre-auth + non-tenant surfaces — sourced from the shared
  // HIDDEN_ROUTE_PREFIXES module so all three chrome consumers (HeaderControls,
  // FeedbackWidget, ChatCompanion) stay aligned.
  const isOnHiddenRoute = isHiddenChromeRoute(pathname ?? "")

  // ── Drag & resize state ────────────────────────────────────────────────
  const [corner, setCorner] = useState<Corner>("bottom-right")
  const [geometry, setGeometry] = useState<PipGeometry>({ x: 0, y: 0, w: DEFAULT_W, h: DEFAULT_H })
  const panelSize = { w: geometry.w, h: geometry.h }
  const position = { x: geometry.x, y: geometry.y }

  // Hydrate from localStorage after mount to avoid SSR/client mismatch
  const pipHydrated = useRef(false)
  // legitimate external sync: localStorage-persisted corner/size/position on mount (SSR-safe hydration)
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (pipHydrated.current) return
    pipHydrated.current = true

    const savedCorner = localStorage.getItem("ai-chat-pip-corner")
    const c: Corner =
      savedCorner === "bottom-right" || savedCorner === "bottom-left" ||
      savedCorner === "top-right" || savedCorner === "top-left"
        ? savedCorner
        : "bottom-right"

    const size = getSavedSize()

    const nextPosition = getCornerPosition(c, size.w, size.h)
    setCorner(c)
    setGeometry(calculatePipGeometry(
      { ...nextPosition, ...size },
      { w: window.innerWidth, h: window.innerHeight },
    ))
  }, [])
  const [isDragging, setIsDragging] = useState(false)
  /* eslint-enable react-hooks/set-state-in-effect */
  const [isResizing, setIsResizing] = useState(false)
  const dragStartRef = useRef({ x: 0, y: 0, posX: 0, posY: 0 })
  const resizeStartRef = useRef({
    x: 0,
    y: 0,
    w: 0,
    h: 0,
    posX: 0,
    posY: 0,
    edge: "" as PipResizeEdge | "",
  })
  const panelRef = useRef<HTMLDivElement>(null)

  // Persist size
  useEffect(() => {
    localStorage.setItem("ai-chat-pip-size", JSON.stringify(panelSize))
  }, [geometry.w, geometry.h])

  // Broadcast chat-panel open/close so the FAB stack can hide itself while
  // a chat surface is on screen (it lives near the same corner).
  useEffect(() => {
    const eventName = ctx.companionMode === "closed" ? "fab-widget:close" : "fab-widget:open"
    window.dispatchEvent(new CustomEvent(eventName, { detail: "chat" }))
  }, [ctx.companionMode])

  // Recalculate position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (ctx.companionMode === "pip") {
        setGeometry((current) => {
          const size = clampPipSize(current.w, current.h, window.innerWidth, window.innerHeight)
          const nextPosition = getCornerPosition(corner, size.w, size.h)
          return calculatePipGeometry(
            { ...nextPosition, ...size },
            { w: window.innerWidth, h: window.innerHeight },
          )
        })
      }
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [corner, ctx.companionMode])

  // ── Drag handlers ──────────────────────────────────────────────────────
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      setIsDragging(true)
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        posX: position.x,
        posY: position.y,
      }
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    },
    [position]
  )

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return
      const dx = e.clientX - dragStartRef.current.x
      const dy = e.clientY - dragStartRef.current.y
      setGeometry((current) => calculatePipGeometry(
        {
          ...current,
          x: dragStartRef.current.posX + dx,
          y: dragStartRef.current.posY + dy,
        },
        { w: window.innerWidth, h: window.innerHeight },
      ))
    },
    [isDragging, panelSize]
  )

  const handlePointerUp = useCallback(() => {
    if (!isDragging) return
    setIsDragging(false)
    const snappedCorner = nearestCorner(position.x, position.y, panelSize.w, panelSize.h)
    setCorner(snappedCorner)
    const nextPosition = getCornerPosition(snappedCorner, panelSize.w, panelSize.h)
    setGeometry((current) => calculatePipGeometry(
      { ...current, ...nextPosition },
      { w: window.innerWidth, h: window.innerHeight },
    ))
    localStorage.setItem("ai-chat-pip-corner", snappedCorner)
  }, [isDragging, position, panelSize])

  // ── Resize handlers ────────────────────────────────────────────────────
  const handleResizeStart = useCallback(
    (e: React.PointerEvent, edge: PipResizeEdge) => {
      e.preventDefault()
      e.stopPropagation()
      setIsResizing(true)
      resizeStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        w: panelSize.w,
        h: panelSize.h,
        posX: position.x,
        posY: position.y,
        edge,
      }
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    },
    [panelSize, position]
  )

  const handleResizeMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isResizing) return
      const { x: sx, y: sy, w: sw, h: sh, posX, posY, edge } = resizeStartRef.current
      const dx = e.clientX - sx
      const dy = e.clientY - sy

      if (!edge) return
      setGeometry(calculatePipGeometry(
        { x: posX, y: posY, w: sw, h: sh },
        { w: window.innerWidth, h: window.innerHeight },
        { edge, dx, dy },
      ))
    },
    [isResizing]
  )

  const handleResizeEnd = useCallback(() => {
    if (!isResizing) return
    setIsResizing(false)
  }, [isResizing])

  // ── Companion mode helpers ─────────────────────────────────────────────
  const closePip = () => ctx.setCompanionMode("closed")
  const maximize = () => ctx.setCompanionMode("maximized")
  const openDrawer = () => ctx.setCompanionMode("drawer")
  const minimize = () => {
    const nextPosition = getCornerPosition(corner, panelSize.w, panelSize.h)
    setGeometry((current) => calculatePipGeometry(
      { ...current, ...nextPosition },
      { w: window.innerWidth, h: window.innerHeight },
    ))
    ctx.setCompanionMode("pip")
  }

  // ── Drawer conversation dropdown state (must be before early returns) ─
  const [drawerDropdownOpen, setDrawerDropdownOpen] = useState(false)
  const [drawerSearch, setDrawerSearch] = useState("")
  const drawerDropdownRef = useRef<HTMLDivElement>(null)
  const drawerSearchRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!drawerDropdownOpen) return
    const handleClick = (e: MouseEvent) => {
      if (drawerDropdownRef.current && !drawerDropdownRef.current.contains(e.target as Node)) {
        setDrawerDropdownOpen(false)
        setDrawerSearch("")
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [drawerDropdownOpen])

  useEffect(() => {
    if (drawerDropdownOpen) {
      setTimeout(() => drawerSearchRef.current?.focus(), 50)
    }
  }, [drawerDropdownOpen])

  useEffect(() => {
    if (!drawerDropdownOpen) return
    const timer = setTimeout(() => ctx.fetchConversations(drawerSearch || undefined), 200)
    return () => clearTimeout(timer)
  }, [drawerSearch, drawerDropdownOpen])

  // ── Drawer resize state (must be before early returns) ──────────────
  const drawerResizing = useRef(false)
  const drawerDragStartX = useRef(0)
  const drawerDragStartW = useRef(0)
  const drawerDragDirection = useRef<TextDirection>("ltr")

  const onDrawerResizeDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault()
    drawerResizing.current = true
    drawerDragStartX.current = e.clientX
    drawerDragStartW.current = ctx.drawerWidth
    // The side the stylesheet docked the drawer on, read at the press.
    drawerDragDirection.current = layoutDirectionOf(e.currentTarget)
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }, [ctx.drawerWidth])

  const onDrawerResizeMove = useCallback((e: React.PointerEvent) => {
    if (!drawerResizing.current) return
    ctx.setDrawerWidth(
      drawerDragStartW.current + drawerWidthDelta(drawerDragStartX.current, e.clientX, drawerDragDirection.current),
    )
  }, [ctx.setDrawerWidth])

  const onDrawerResizeUp = useCallback(() => {
    drawerResizing.current = false
  }, [])

  // First-open drawer width. The context defaults to a fixed 420px, which is
  // the "input/output window very small" complaint on any real monitor. Only
  // applied when the user has never dragged the drawer edge.
  const drawerWidthSeeded = useRef(false)
  useEffect(() => {
    if (drawerWidthSeeded.current) return
    drawerWidthSeeded.current = true
    if (localStorage.getItem("ai-chat-drawer-width") === null) {
      ctx.setDrawerWidth(getInitialDrawerWidth(window.innerWidth))
    }
  }, [ctx.setDrawerWidth])

  // Don't render on the dedicated chat page
  if (isOnChatPage) return null

  // Don't render on pre-auth or non-tenant surfaces (login, onboarding,
  // admin/super-admin, portal, etc.). Mirrors auth-guard's PUBLIC_PATHS.
  if (isOnHiddenRoute) return null

  // ── FAB (closed mode) — draggable floating button ─────────────────────
  if (ctx.companionMode === "closed") {
    return null
  }

  // ── Maximized mode ────────────────────────────────────────────────────
  if (ctx.companionMode === "maximized") {
    return (
      <>
        {/* Backdrop — click to minimize so the underlying sidebar/page is never
            a permanent dead-end (2026-05-29). The z-40 backdrop sits above the
            z-10 app sidebar; without a dismiss handler a maximized chat covered
            the sidebar with no way out. Restores the standard modal contract. */}
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm"
          onClick={minimize}
          aria-hidden="true"
        />

        {/* Full overlay panel */}
        <div data-slot="chat-companion" className="fixed inset-4 z-50 flex flex-col rounded-2xl border border-border bg-background shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-muted/30">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => ctx.setChatSidebarOpen((v: boolean) => !v)}
              title={ctx.chatSidebarOpen ? "Hide conversations" : "Show conversations"}
            >
              {/* The list sits at the inline start (border-e), the right in
                  Arabic, so the panel icon mirrors with it. */}
              {ctx.chatSidebarOpen ? (
                <PanelLeftClose className="h-3.5 w-3.5 rtl-flip" />
              ) : (
                <PanelLeftOpen className="h-3.5 w-3.5 rtl-flip" />
              )}
            </Button>
            <MessageSquare className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm font-medium flex-1 truncate">
              {ctx.conversations.find((c) => c.id === ctx.activeId)?.title || "AI Chat"}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => ctx.handleNewChat()}
                title="New chat"
                disabled={ctx.isStreaming}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
              <button
                className="flex items-center gap-1.5 px-2 h-7 rounded text-xs font-medium transition-colors hover:bg-accent"
                onClick={() => ctx.setAutoNavigate(!ctx.autoNavigate)}
                title={ctx.autoNavigate ? "Auto-navigate: ON" : "Auto-navigate: OFF"}
              >
                <Compass className="h-3.5 w-3.5" />
                <span>Auto-Navigate</span>
                <span
                  className={cn(
                    "text-[10px] font-semibold px-1.5 py-0.5 rounded",
                    ctx.autoNavigate
                      ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {ctx.autoNavigate ? "ON" : "OFF"}
                </span>
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={openDrawer}
                title="Dock to side"
              >
                <DockRightIcon className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={minimize}
                title="Minimize to PiP"
              >
                <Minimize2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={closePip}
                title="Close chat"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Body: Sidebar + Chat */}
          <div className="flex-1 flex min-h-0 overflow-hidden">
            {/* Conversation sidebar */}
            <div
              className={cn(
                // Proportional, not a fixed 280px: a fixed sidebar subtracts the
                // same absolute width no matter how narrow the host surface is,
                // which is what left the conversation column unreadably thin.
                "border-e border-border shrink-0 transition-all duration-200 overflow-hidden",
                ctx.chatSidebarOpen ? "w-[clamp(200px,22%,300px)]" : "w-0"
              )}
            >
              {ctx.chatSidebarOpen && (
                <ChatSidebar
                  conversations={ctx.conversations}
                  activeId={ctx.activeId}
                  onSelect={(id) => ctx.loadConversation(id)}
                  onNewChat={ctx.handleNewChat}
                  onDelete={(id) => ctx.handleDelete(id)}
                  onTogglePin={(id) => ctx.handleTogglePin(id)}
                  onToggleStar={(id) => ctx.handleToggleStar(id)}
                  onSearch={(q) => ctx.fetchConversations(q)}
                  disabled={ctx.isStreaming}
                />
              )}
            </div>

            {/* Chat area */}
            <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
              {aiNotices}
              {ctx.messages.length === 0 && !ctx.isStreaming ? (
                <ChatWelcome onSuggestion={ctx.handleSuggestion} />
              ) : (
                <ChatMessageList
                  messages={ctx.messages}
                  streamingContent={ctx.streamingContent}
                  isStreaming={ctx.isStreaming}
                  streamingToolCalls={ctx.streamingToolCalls}
                  onRegenerate={ctx.handleRegenerate}
                  onEditMessage={ctx.handleEditMessage}
                  conversationId={ctx.activeId}
                />
              )}

              <ChatInput
                value={ctx.inputValue}
                onChange={ctx.setInputValue}
                onSend={() => ctx.sendMessage(ctx.inputValue)}
                onStop={ctx.handleStop}
                isStreaming={ctx.isStreaming}
                disabled={composerDisabled}
              />
            </div>
          </div>
        </div>

        {ctx.pendingVerificationRequirement === 'step-up' ? (
          <VerificationDialog
            open={ctx.verificationOpen}
            onOpenChange={(v) => !v && ctx.handleVerificationCancel()}
            toolName={ctx.pendingVerificationTool}
            toolLabel={ctx.pendingVerificationToolLabel}
            input={ctx.pendingVerificationInputRef.current}
            conversationId={ctx.activeId ?? ""}
            requirement="step-up"
            factors={ctx.pendingVerificationFactors}
            verifyReason={ctx.pendingVerificationReason}
            message={ctx.pendingVerificationMessage}
            onConfirm={ctx.handleVerified}
            onCancel={ctx.handleVerificationCancel}
          />
        ) : (
          <VerificationDialog
            open={ctx.verificationOpen}
            onOpenChange={(v) => !v && ctx.handleVerificationCancel()}
            toolName={ctx.pendingVerificationTool}
            toolLabel={ctx.pendingVerificationToolLabel}
            input={ctx.pendingVerificationInputRef.current}
            conversationId={ctx.activeId ?? ""}
            requirement="confirm"
            message={ctx.pendingVerificationMessage}
            onConfirm={ctx.handleVerified}
            onCancel={ctx.handleVerificationCancel}
          />
        )}
      </>
    )
  }

  // ── Drawer mode ────────────────────────────────────────────────────────
  if (ctx.companionMode === "drawer") {
    return (
      <>
        <div
          data-slot="chat-companion"
          // Docked at the inline end (the left, in Arabic, where the app
          // sidebar is on the right); the module layouts reserve it with
          // marginInlineEnd.
          className="fixed top-0 end-0 bottom-0 z-50 flex flex-col border-s border-border bg-background shadow-lg overflow-hidden"
          style={{ width: ctx.drawerWidth }}
        >
          {/* Resize handle on the edge that faces the page (the inline start);
              drawerWidthDelta widens the drawer as the pointer moves that way. */}
          <div
            className="absolute top-0 bottom-0 start-0 w-1.5 z-[60] cursor-ew-resize hover:bg-primary/10 active:bg-primary/20 transition-colors"
            onPointerDown={onDrawerResizeDown}
            onPointerMove={onDrawerResizeMove}
            onPointerUp={onDrawerResizeUp}
          />

          {/* Header */}
          <div className="flex items-center gap-2 px-4 h-16 shrink-0 border-b border-border bg-background/80 backdrop-blur-md transition-[height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
            <button
              className={cn(
                "flex items-center gap-1.5 text-sm font-medium truncate rounded-md px-2 py-1.5 transition-colors",
                drawerDropdownOpen ? "bg-accent" : "hover:bg-accent"
              )}
              onClick={() => setDrawerDropdownOpen((v) => !v)}
              title="Switch conversation"
              disabled={ctx.isStreaming}
            >
              <MessageSquare className="h-4 w-4 text-primary shrink-0" />
              <span className="truncate max-w-[180px]">
                {ctx.conversations.find((c) => c.id === ctx.activeId)?.title || "New conversation"}
              </span>
              <ChevronDown className={cn(
                "h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform",
                drawerDropdownOpen && "rotate-180"
              )} />
            </button>

            <div className="flex items-center gap-1 ms-auto">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => { ctx.handleNewChat(); setDrawerDropdownOpen(false) }}
                title="New chat"
                disabled={ctx.isStreaming}
              >
                <Plus className="h-4 w-4" />
              </Button>
              {/* Labelled, not a bare 14px glyph: the near-fullscreen mode was
                  effectively undiscoverable behind an unlabelled icon, so users
                  stayed in the narrow drawer and reported it as "too small". */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 px-2 text-xs"
                onClick={maximize}
                title="Expand to full screen"
              >
                <Maximize2 className="h-3.5 w-3.5" />
                <span>Expand</span>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={closePip}
                title="Close"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Conversation dropdown */}
          {drawerDropdownOpen && (
            <div
              ref={drawerDropdownRef}
              className="border-b border-border bg-background shadow-sm max-h-[340px] flex flex-col shrink-0 z-[70]"
            >
              <div className="flex items-center gap-2 px-3 py-2 border-b border-border/60 shrink-0">
                <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <input
                  ref={drawerSearchRef}
                  type="text"
                  value={drawerSearch}
                  onChange={(e) => setDrawerSearch(e.target.value)}
                  placeholder="Search..."
                  className="flex-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                />
              </div>

              <div className="flex-1 overflow-y-auto min-h-0">
                <DrawerConversationList
                  conversations={ctx.conversations}
                  activeId={ctx.activeId}
                  onSelect={(id) => {
                    ctx.loadConversation(id)
                    setDrawerDropdownOpen(false)
                    setDrawerSearch("")
                  }}
                  disabled={ctx.isStreaming}
                />
              </div>

              <button
                onClick={() => { ctx.handleNewChat(); setDrawerDropdownOpen(false); setDrawerSearch("") }}
                disabled={ctx.isStreaming}
                className="flex items-center gap-2 px-4 py-2 border-t border-border text-sm font-medium text-primary hover:bg-accent transition-colors shrink-0"
              >
                <Plus className="h-3.5 w-3.5" />
                New conversation
              </button>
            </div>
          )}

          {/* Chat body */}
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            {aiNotices}
            {ctx.messages.length === 0 && !ctx.isStreaming ? (
              <ChatWelcome onSuggestion={ctx.handleSuggestion} />
            ) : (
              <ChatMessageList
                messages={ctx.messages}
                streamingContent={ctx.streamingContent}
                isStreaming={ctx.isStreaming}
                streamingToolCalls={ctx.streamingToolCalls}
                onRegenerate={ctx.handleRegenerate}
                onEditMessage={ctx.handleEditMessage}
                conversationId={ctx.activeId}
              />
            )}

            <ChatInput
              value={ctx.inputValue}
              onChange={ctx.setInputValue}
              onSend={() => ctx.sendMessage(ctx.inputValue)}
              onStop={ctx.handleStop}
              isStreaming={ctx.isStreaming}
              disabled={composerDisabled}
            />
          </div>
        </div>

        {ctx.pendingVerificationRequirement === 'step-up' ? (
          <VerificationDialog
            open={ctx.verificationOpen}
            onOpenChange={(v) => !v && ctx.handleVerificationCancel()}
            toolName={ctx.pendingVerificationTool}
            toolLabel={ctx.pendingVerificationToolLabel}
            input={ctx.pendingVerificationInputRef.current}
            conversationId={ctx.activeId ?? ""}
            requirement="step-up"
            factors={ctx.pendingVerificationFactors}
            verifyReason={ctx.pendingVerificationReason}
            message={ctx.pendingVerificationMessage}
            onConfirm={ctx.handleVerified}
            onCancel={ctx.handleVerificationCancel}
          />
        ) : (
          <VerificationDialog
            open={ctx.verificationOpen}
            onOpenChange={(v) => !v && ctx.handleVerificationCancel()}
            toolName={ctx.pendingVerificationTool}
            toolLabel={ctx.pendingVerificationToolLabel}
            input={ctx.pendingVerificationInputRef.current}
            conversationId={ctx.activeId ?? ""}
            requirement="confirm"
            message={ctx.pendingVerificationMessage}
            onConfirm={ctx.handleVerified}
            onCancel={ctx.handleVerificationCancel}
          />
        )}
      </>
    )
  }

  // ── PiP mode ──────────────────────────────────────────────────────────
  const resizeHandleProps = (edge: PipResizeEdge, cursor: string) => ({
    onPointerDown: (e: React.PointerEvent) => handleResizeStart(e, edge),
    onPointerMove: handleResizeMove,
    onPointerUp: handleResizeEnd,
    style: { cursor } as React.CSSProperties,
  })

  const glowInset = 5

  return (
    <>
      {/* Aura glow — rendered behind the panel at z-49 */}
      <div
        className={cn(
          "fixed z-[51] rounded-[1.1rem] pointer-events-none",
          (isDragging || isResizing)
            ? "opacity-0"
            : ctx.autoNavigate
              ? "pip-aura-active"
              : "pip-aura-inactive"
        )}
        style={{
          left: position.x - glowInset,
          top: position.y - glowInset,
          width: panelSize.w + glowInset * 2,
          height: panelSize.h + glowInset * 2,
          transition: (isDragging || isResizing)
            ? "opacity 150ms"
            : "left 300ms ease-out, top 300ms ease-out, opacity 500ms ease-in-out, filter 500ms ease-in-out",
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        data-slot="chat-companion"
        className={cn(
          "fixed z-[52] flex flex-col rounded-2xl border bg-background pip-compact",
          isDragging ? "cursor-grabbing" : "cursor-default",
          (isDragging || isResizing)
            ? "shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)]"
            : ctx.autoNavigate
              ? "pip-border-active"
              : "pip-border-inactive"
        )}
        style={{
          left: position.x,
          top: position.y,
          width: panelSize.w,
          height: panelSize.h,
          transition: (isDragging || isResizing)
            ? "box-shadow 150ms"
            : "left 300ms ease-out, top 300ms ease-out, box-shadow 400ms ease-in-out, border-color 400ms ease-in-out",
        }}
      >
        {/* Resize handles — edges */}
        <div className="absolute inset-x-2 top-0 h-1.5 z-10" {...resizeHandleProps("t", "ns-resize")} />
        <div className="absolute inset-x-2 bottom-0 h-1.5 z-10" {...resizeHandleProps("b", "ns-resize")} />
        {/* rtl-codemod-ignore-next-line: each handle names a physical edge ("l", "tr", ...) of a panel placed at an inline `left` */}
        <div className="absolute inset-y-2 left-0 w-1.5 z-10" {...resizeHandleProps("l", "ew-resize")} />
        {/* rtl-codemod-ignore-next-line */}
        <div className="absolute inset-y-2 right-0 w-1.5 z-10" {...resizeHandleProps("r", "ew-resize")} />
        {/* Resize handles — corners (rtl-codemod-ignore-next-line) */}
        <div className="absolute top-0 left-0 w-3 h-3 z-20" {...resizeHandleProps("tl", "nwse-resize")} />
        {/* rtl-codemod-ignore-next-line */}
        <div className="absolute top-0 right-0 w-3 h-3 z-20" {...resizeHandleProps("tr", "nesw-resize")} />
        {/* rtl-codemod-ignore-next-line */}
        <div className="absolute bottom-0 left-0 w-3 h-3 z-20" {...resizeHandleProps("bl", "nesw-resize")} />
        {/* rtl-codemod-ignore-next-line */}
        <div className="absolute bottom-0 right-0 w-3 h-3 z-20" {...resizeHandleProps("br", "nwse-resize")} />

        {/* Drag handle + header */}
        <div
          className="flex items-center gap-1.5 px-2.5 py-2.5 border-b border-border bg-muted/30 cursor-grab active:cursor-grabbing select-none shrink-0"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <GripHorizontal className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
          <MessageSquare className="h-3 w-3 text-primary shrink-0" />
          <span className="text-[11px] font-medium flex-1 truncate">
            {ctx.conversations.find((c) => c.id === ctx.activeId)?.title || "AI Chat"}
          </span>
          <div className="flex items-center gap-0">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={() => ctx.handleNewChat()}
              title="New chat"
              disabled={ctx.isStreaming}
            >
              <Plus className="h-2.5 w-2.5" />
            </Button>
            <button
              className="flex items-center gap-0.5 px-1 h-5 rounded text-[9px] font-medium transition-colors hover:bg-accent"
              onClick={() => ctx.setAutoNavigate(!ctx.autoNavigate)}
              title={ctx.autoNavigate ? "Auto-navigate: ON" : "Auto-navigate: OFF"}
            >
              <Compass className="h-2.5 w-2.5" />
              <span>Auto-Nav</span>
              <span
                className={cn(
                  "text-[8px] font-bold px-0.5 rounded",
                  ctx.autoNavigate
                    ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
                    : "bg-muted text-muted-foreground"
                )}
              >
                {ctx.autoNavigate ? "ON" : "OFF"}
              </span>
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={openDrawer}
              title="Dock to side"
            >
              <DockRightIcon className="h-2.5 w-2.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={maximize}
              title="Maximize to full screen"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={() => {
                ctx.setCompanionMode("closed")
              }}
              title="Close"
            >
              <X className="h-2.5 w-2.5" />
            </Button>
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
          {/* PiP is the smallest surface, and it is the one where a silently
              grey composer is most likely to read as "the panel is broken". */}
          {aiNotices}
          {ctx.messages.length === 0 && !ctx.isStreaming ? (
            <CompactWelcome onSuggestion={ctx.handleSuggestion} />
          ) : (
            <ChatMessageList
              messages={ctx.messages}
              streamingContent={ctx.streamingContent}
              isStreaming={ctx.isStreaming}
              streamingToolCalls={ctx.streamingToolCalls}
              onRegenerate={ctx.handleRegenerate}
              onEditMessage={ctx.handleEditMessage}
              compact
              conversationId={ctx.activeId}
            />
          )}

          {/* Compact input for PiP.
              Was a hand-rolled one-line textarea with no auto-grow handler, no
              length cap and no IME guard — the "input window very small"
              report. ChatInput's compact variant carries all of those. */}
          <ChatInput
            value={ctx.inputValue}
            onChange={ctx.setInputValue}
            onSend={() => ctx.sendMessage(ctx.inputValue)}
            onStop={ctx.handleStop}
            isStreaming={ctx.isStreaming}
            disabled={composerDisabled}
            compact
          />
        </div>
      </div>

      {ctx.pendingVerificationRequirement === 'step-up' ? (
        <VerificationDialog
          open={ctx.verificationOpen}
          onOpenChange={(v) => !v && ctx.handleVerificationCancel()}
          toolName={ctx.pendingVerificationTool}
          toolLabel={ctx.pendingVerificationToolLabel}
          input={ctx.pendingVerificationInputRef.current}
          conversationId={ctx.activeId ?? ""}
          requirement="step-up"
          factors={ctx.pendingVerificationFactors}
          verifyReason={ctx.pendingVerificationReason}
          message={ctx.pendingVerificationMessage}
          onConfirm={ctx.handleVerified}
          onCancel={ctx.handleVerificationCancel}
        />
      ) : (
        <VerificationDialog
          open={ctx.verificationOpen}
          onOpenChange={(v) => !v && ctx.handleVerificationCancel()}
          toolName={ctx.pendingVerificationTool}
          toolLabel={ctx.pendingVerificationToolLabel}
          input={ctx.pendingVerificationInputRef.current}
          conversationId={ctx.activeId ?? ""}
          requirement="confirm"
          message={ctx.pendingVerificationMessage}
          onConfirm={ctx.handleVerified}
          onCancel={ctx.handleVerificationCancel}
        />
      )}
    </>
  )
}

// ─── Drawer conversation list (grouped by time) ─────────────────────────────
function DrawerConversationList({
  conversations,
  activeId,
  onSelect,
  disabled,
}: {
  conversations: { id: string; title: string; updatedAt: string }[]
  activeId: string | null
  onSelect: (id: string) => void
  disabled: boolean
}) {
  const DAY = 86400000

  const groups: { label: string; items: typeof conversations }[] = []
  const today: typeof conversations = []
  const yesterday: typeof conversations = []
  const thisWeek: typeof conversations = []
  const older: typeof conversations = []

  // Pin "now" to mount-time via lazy useState initializer so render stays pure.
  // Acceptable staleness for a conversation list grouping.
  const [now] = useState(() => Date.now())

  for (const c of conversations) {
    const age = now - new Date(c.updatedAt).getTime()
    if (age < DAY) today.push(c)
    else if (age < 2 * DAY) yesterday.push(c)
    else if (age < 7 * DAY) thisWeek.push(c)
    else older.push(c)
  }

  if (today.length) groups.push({ label: "Today", items: today })
  if (yesterday.length) groups.push({ label: "Yesterday", items: yesterday })
  if (thisWeek.length) groups.push({ label: "This week", items: thisWeek })
  if (older.length) groups.push({ label: "Older", items: older })

  const timeAgo = (ts: string) => {
    const diff = now - new Date(ts).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return "now"
    if (mins < 60) return `${mins}m`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h`
    const days = Math.floor(hrs / 24)
    return `${days}d`
  }

  if (groups.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <MessageSquare className="h-6 w-6 mb-2 opacity-40" />
        <p className="text-xs">No conversations found</p>
      </div>
    )
  }

  return (
    <>
      {groups.map((group) => (
        <div key={group.label}>
          <div className="px-3 pt-2 pb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
            {group.label}
          </div>
          {group.items.map((c) => (
            <button
              key={c.id}
              onClick={() => onSelect(c.id)}
              disabled={disabled}
              className={cn(
                "w-full flex items-center justify-between gap-2 px-3 py-1.5 text-start text-[13px] transition-colors",
                c.id === activeId
                  ? "bg-accent font-medium"
                  : "hover:bg-accent/50"
              )}
            >
              <span className="truncate flex-1">{c.title}</span>
              <span className="text-[10px] text-muted-foreground shrink-0">{timeAgo(c.updatedAt)}</span>
            </button>
          ))}
        </div>
      ))}
    </>
  )
}
